import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  PlusIcon,
  PencilIcon,
  PowerIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import PaginationFooter from '@/components/PaginationFooter';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { confirmDialog } from '../components/ConfirmDialog';

interface PaymentMethod {
  id: string;
  name: string;
  code: string;
  description?: string;
  surcharge: number;
  enabled: boolean;
  isDefault: boolean;
  transferAccountHolder?: string;
  transferBankName?: string;
  transferAccountType?: string;
  transferAccountNumber?: string;
  transferAlias?: string;
  transferCuit?: string;
}

interface PaymentMethodForm {
  name: string;
  code: string;
  description: string;
  surcharge: number;
  enabled: boolean;
  isDefault: boolean;
  transferAccountHolder: string;
  transferBankName: string;
  transferAccountType: string;
  transferAccountNumber: string;
  transferAlias: string;
  transferCuit: string;
}

const defaultFormValues: PaymentMethodForm = {
  name: '',
  code: '',
  description: '',
  surcharge: 0,
  enabled: true,
  isDefault: false,
  transferAccountHolder: '',
  transferBankName: '',
  transferAccountType: '',
  transferAccountNumber: '',
  transferAlias: '',
  transferCuit: ''
};

const PaymentMethods: React.FC = () => {
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<PaymentMethodForm>({
    defaultValues: defaultFormValues
  });

  const watchedCode = watch('code');
  const watchedName = watch('name');
  const normalizePaymentCode = (value: string) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  const isTransferMethod =
    normalizePaymentCode(watchedCode) === 'transferencia' ||
    normalizePaymentCode(watchedName).includes('transfer');

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings/payment-methods');
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.paymentMethods)
          ? response.data.paymentMethods
          : Array.isArray(response.data?.methods)
            ? response.data.methods
            : [];
      setPaymentMethods(data);
    } catch (error) {
      console.error('Error al cargar métodos de pago:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategory = (method: PaymentMethod) => {
    const contadoCodes = new Set(['efectivo', 'cash', 'qr', 'tarjeta de debito', 'tarjeta de débito', 'debito', 'td', 'transferencia']);
    const code = (method.code || '').toLowerCase();
    const name = (method.name || '').toLowerCase();
    if (contadoCodes.has(code) || contadoCodes.has(name)) return 'Contado';
    return '';
  };

  const onSubmit = async (data: PaymentMethodForm) => {
    try {
      setSaving(true);
      const payload = {
        ...data,
        name: data.name.trim(),
        code: data.code.trim(),
        surcharge: Number(data.surcharge ?? 0) || 0,
        transferAccountHolder: data.transferAccountHolder?.trim() || '',
        transferBankName: data.transferBankName?.trim() || '',
        transferAccountType: data.transferAccountType?.trim() || '',
        transferAccountNumber: data.transferAccountNumber?.trim() || '',
        transferAlias: data.transferAlias?.trim() || '',
        transferCuit: data.transferCuit?.trim() || ''
      };

      if (editingMethod) {
        await axios.put(`/api/settings/payment-methods/${editingMethod.id}`, payload);
      } else {
        await axios.post('/api/settings/payment-methods', payload);
      }

      await fetchPaymentMethods();
      setEditingMethod(null);
      reset(defaultFormValues);
      setShowFormModal(false);
    } catch (error) {
      console.error('Error al guardar el método de pago:', error);
    } finally {
      setSaving(false);
    }
  };

  const togglePaymentMethod = async (id: string, enabled: boolean) => {
    try {
      await axios.patch(`/api/settings/payment-methods/${id}`, { enabled });
      await fetchPaymentMethods();
    } catch (error) {
      console.error('Error al cambiar estado del método de pago:', error);
    }
  };

  const deletePaymentMethod = async (id: string) => {
    const confirmDelete = await confirmDialog('¿Eliminar este método de pago?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`/api/settings/payment-methods/${id}`);
      await fetchPaymentMethods();
    } catch (error) {
      console.error('Error al eliminar método de pago:', error);
    }
  };

  const handleEdit = (method: PaymentMethod) => {
    setEditingMethod(method);
    reset({
      name: method.name || '',
      code: method.code || '',
      description: method.description || '',
      surcharge: typeof method.surcharge === 'number' ? method.surcharge : 0,
      enabled: method.enabled,
      isDefault: method.isDefault,
      transferAccountHolder: method.transferAccountHolder || '',
      transferBankName: method.transferBankName || '',
      transferAccountType: method.transferAccountType || '',
      transferAccountNumber: method.transferAccountNumber || '',
      transferAlias: method.transferAlias || '',
      transferCuit: method.transferCuit || ''
    });
    setShowFormModal(true);
  };

  const handleCancel = () => {
    setEditingMethod(null);
    reset(defaultFormValues);
    setShowFormModal(false);
  };

  const cleanName = (name: string) => {
    if (typeof name !== 'string') return name;
    return name.replace(/\s*\d+\s*$/, '').trim() || name.trim();
  };

  // Paginación
  const totalPages = Math.ceil(paymentMethods.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMethods = paymentMethods.slice(startIndex, endIndex);

  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  const totalResults = paymentMethods.length;
  const startResult = totalResults === 0 ? 0 : startIndex + 1;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <button onClick={() => navigate('/settings')} className="text-left">
                  Configuración
                </button>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Medios de Pago</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate('/settings')}>
            Volver
          </Button>
          <Button
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
            onClick={() => {
              setEditingMethod(null);
              reset(defaultFormValues);
              setShowFormModal(true);
            }}
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo método
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </Button>
        </div>

        <div className="space-y-4">
          <Badge variant="secondary" className="w-fit">
            Total: {paymentMethods.length}
          </Badge>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="h-10 w-10 rounded-full border border-border border-t-transparent animate-spin" />
              <p className="text-muted-foreground mt-4">Cargando métodos...</p>
            </div>
          ) : paymentMethods.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <p className="text-sm text-muted-foreground">No hay métodos configurados.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Crea el primero usando el botón Nuevo método.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left w-[24%]">Nombre</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left w-[16%]">Codigo</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left w-[16%]">Categoria</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left w-[20%]">Recargo/Descuento</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left w-[12%]">Estado</TableHead>
                    <TableHead className="text-right h-10 px-2 align-middle font-medium whitespace-nowrap w-[12%]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedMethods.map((method, idx) => {
                    const displayName = cleanName(method.name);
                    const hasSurcharge = Number(method.surcharge) !== 0;
                    const surchargeLabel = hasSurcharge
                      ? method.surcharge > 0
                        ? `+${method.surcharge}%`
                        : `${method.surcharge}%`
                      : '-';
                    const surchargeClass = hasSurcharge
                      ? method.surcharge > 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-green-600 dark:text-green-400'
                      : 'text-muted-foreground';

                    return (
                      <TableRow key={method.id || idx} className="border-b hover:bg-muted/50">
                        <TableCell className="p-2 align-middle whitespace-nowrap space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground text-sm">{displayName || 'Sin nombre'}</span>
                            {Boolean(method.isDefault) && (
                              <span className="rounded-full border border-border px-2 py-0.5 text-[11px]">
                                Por defecto
                              </span>
                            )}
                          </div>
                          {method.description && (
                            <p className="text-xs text-muted-foreground leading-tight whitespace-normal">{method.description}</p>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm p-2 align-middle whitespace-nowrap">
                          {method.code}
                        </TableCell>
                        <TableCell className="text-sm p-2 align-middle whitespace-nowrap">
                          {getCategory(method) ? (
                            <Badge variant="secondary" className="rounded-full px-2 py-0.5 text-xs">
                              {getCategory(method)}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm p-2 align-middle whitespace-nowrap">
                          <span className={surchargeClass}>{surchargeLabel}</span>
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] ${
                              method.enabled
                                ? 'bg-black text-white dark:bg-white dark:text-black'
                                : 'border border-border text-muted-foreground'
                            }`}
                          >
                            {method.enabled ? 'Activo' : 'Inactivo'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right p-2 align-middle whitespace-nowrap">
                          <div className="flex justify-end gap-2 items-center">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="size-8"
                                  onClick={() => handleEdit(method)}
                                  aria-label="Editar"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Editar</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="size-8"
                                  onClick={() => togglePaymentMethod(method.id, !method.enabled)}
                                  aria-label={method.enabled ? 'Desactivar' : 'Activar'}
                                >
                                  <PowerIcon className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{method.enabled ? 'Desactivar' : 'Activar'}</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-8"
                                  onClick={() => deletePaymentMethod(method.id)}
                                  aria-label="Eliminar"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Eliminar</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <PaginationFooter
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={totalResults}
            startResult={startResult}
            onFirst={goToFirstPage}
            onPrev={goToPreviousPage}
            onNext={goToNextPage}
            onLast={goToLastPage}
            isLoading={loading}
          />
        </div>
      </div>

      <Dialog
        open={showFormModal}
        onOpenChange={(open) => {
          if (!open) {
            handleCancel();
          } else {
            setShowFormModal(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingMethod ? 'Editar método de pago' : 'Nuevo método de pago'}</DialogTitle>
            <DialogDescription>
              Completa el formulario para crear o actualizar un método de pago.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  placeholder="Ej: Efectivo, Tarjeta 3 cuotas"
                  {...register('name', {
                    required: 'Nombre es requerido',
                    setValueAs: (value) => (typeof value === 'string' ? value.trim() : '')
                  })}
                />
                {errors.name && <p className="text-destructive text-sm">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  placeholder="Ej: efectivo, tc3"
                  {...register('code', {
                    required: 'Código es requerido',
                    setValueAs: (value) => (typeof value === 'string' ? value.trim() : '')
                  })}
                />
                {errors.code && <p className="text-destructive text-sm">{errors.code.message}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                rows={2}
                placeholder="Descripción del método de pago"
                {...register('description')}
              />
            </div>

            {isTransferMethod && (
              <div className="space-y-3 rounded-lg border border-border p-3">
                <p className="text-sm font-medium">Datos para transferencia</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="transferAccountHolder">Titular</Label>
                    <Input id="transferAccountHolder" placeholder="Ej: ROCAM SRL" {...register('transferAccountHolder')} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="transferCuit">CUIT</Label>
                    <Input id="transferCuit" placeholder="Ej: 30716642336" {...register('transferCuit')} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="transferBankName">Banco</Label>
                    <Input id="transferBankName" placeholder="Ej: Banco Nación" {...register('transferBankName')} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="transferAccountType">Tipo de cuenta</Label>
                    <Input id="transferAccountType" placeholder="Ej: Cuenta corriente" {...register('transferAccountType')} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="transferAccountNumber">CBU</Label>
                    <Input id="transferAccountNumber" placeholder="Ej: 2850826730094193312201" {...register('transferAccountNumber')} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="transferAlias">Alias</Label>
                    <Input id="transferAlias" placeholder="Ej: ROCAM.SRL" {...register('transferAlias')} />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="surcharge">Recargo/Descuento (%)</Label>
                <Input
                  id="surcharge"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  {...register('surcharge', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? 0 : Number(value),
                    validate: (value) =>
                      Number.isFinite(value as number) || 'Ingresá un número válido'
                  })}
                />
                <p className="text-xs text-muted-foreground">
                  Positivo = recargo, Negativo = descuento
                </p>
                {errors.surcharge && (
                  <p className="text-destructive text-sm">{errors.surcharge.message}</p>
                )}
              </div>

              <div className="flex flex-col justify-center gap-3 rounded-lg border border-dashed border-border px-4 py-3 bg-muted/40">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register('enabled')}
                    className="h-4 w-4 rounded border-border text-black dark:text-white"
                  />
                  <span>Habilitado</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...register('isDefault')}
                    className="h-4 w-4 rounded border-border text-black dark:text-white"
                  />
                  <span>Por defecto</span>
                </label>
              </div>
            </div>

            <DialogFooter className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90"
                disabled={saving}
              >
                {editingMethod ? 'Guardar cambios' : 'Crear método'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethods;








